import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import { DayOfWeek } from "client/DateAndTime/culture";
import {
	calculateTimeDifference,
	getDayOfWeek,
	getStartOfDay,
	parseDtoDate
} from "client/DateAndTime/dateTimeUtils";
import { convertToTimeOnly } from "client/DateAndTime/timeUtils";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import { TimeZone } from "client/DateAndTime/timezones";
import { useMemo } from "react";

export type WeekScheduleBlocksResult<TItem> = {
	startHour: number;
	endHour: number;
	blocksPerDay: Record<number, readonly DayTimeBlock<TItem>[]>;
};

export type DayTimeBlock<TItem> = {
	appointment: TItem | undefined;
	durationInMinutes: number;
	startTimeInMinutes: number;
	endTimeInMinutes: number;
};

export function useWeekScheduleBlocks<TItem>(
	items: readonly TItem[],
	startDateTimeRetriever: (item: TItem) => string | Date,
	endDateTimeRetriever: (item: TItem) => string | Date,
	timezone: TimeZone
): WeekScheduleBlocksResult<TItem> {

	return useMemo(
		(): WeekScheduleBlocksResult<TItem> => {
			const { startHour, endHour } = getStartAndEndHour(
				items,
				startDateTimeRetriever,
				endDateTimeRetriever,
				timezone
			);

			const blocksPerDay: Record<number, readonly DayTimeBlock<TItem>[]> = prepareBlocksPerDay(
				items,
				startHour,
				startDateTimeRetriever,
				endDateTimeRetriever,
				timezone
			);

			return {
				startHour,
				endHour,
				blocksPerDay
			};
		},
		[
			items,
			startDateTimeRetriever,
			endDateTimeRetriever,
			timezone
		]
	);
}

function getStartAndEndHour<TItem>(
	items: readonly TItem[],
	startDateTimeRetriever: (item: TItem) => string | Date,
	endDateTimeRetriever: (item: TItem) => string | Date,
	timezone: TimeZone
): { startHour: number; endHour: number; } {
	let startHour = 24;
	let endHour = 0;

	for (const item of items) {
		const start: Date = parseDtoDate(startDateTimeRetriever(item));
		const startTime: TimeOnly = convertToTimeOnly(start, timezone);

		const itemStartHour: number = startTime.hour;

		if (itemStartHour < startHour) {
			startHour = itemStartHour;
		}

		const end: Date = parseDtoDate(endDateTimeRetriever(item));
		const endTime: TimeOnly = convertToTimeOnly(end, timezone);

		let itemEndHour: number = endTime.hour;

		if (endTime.hour < startTime.hour) {
			itemEndHour = 24 + itemEndHour;
		}

		if (itemEndHour > endHour) {
			endHour = itemEndHour;
		}
	}

	return {
		startHour,
		endHour
	};
}

function prepareBlocksPerDay<TItem>(
	items: readonly TItem[],
	startHour: number,
	startDateTimeRetriever: (item: TItem) => string | Date,
	endDateTimeRetriever: (item: TItem) => string | Date,
	timezone: TimeZone
): Record<number, readonly DayTimeBlock<TItem>[]> {
	const sortedItems: TItem[] = [...items].sort(
		(a, b) => nativeSortFunction(
			parseDtoDate(startDateTimeRetriever(a)),
			parseDtoDate(startDateTimeRetriever(b)),
			DataSourceSortDirection.Ascending
		)
	);

	const startHourInMinutes: number = startHour * 60;
	const result: Record<number, DayTimeBlock<TItem>[]> = {};

	for (const appointment of sortedItems) {
		const appointmentStart: Date = parseDtoDate(startDateTimeRetriever(appointment));
		const appointmentEnd: Date = parseDtoDate(endDateTimeRetriever(appointment));
		const day: DayOfWeek = getDayOfWeek(appointmentStart, timezone);

		if (!result.hasOwnProperty(day)) {
			result[day] = [];
		}
		const blocksForTheDay: DayTimeBlock<TItem>[] = result[day];

		const previousEndTimeInMinutes: number = blocksForTheDay.length > 0
			? blocksForTheDay[blocksForTheDay.length - 1].endTimeInMinutes
			: startHourInMinutes;

		const appointmentStartTimeInMinutes: number = getMinutesSinceMidnight(appointmentStart, timezone);

		if (appointmentStartTimeInMinutes !== previousEndTimeInMinutes) {
			blocksForTheDay.push({
				appointment: undefined,
				durationInMinutes: appointmentStartTimeInMinutes - previousEndTimeInMinutes,
				startTimeInMinutes: previousEndTimeInMinutes,
				endTimeInMinutes: appointmentStartTimeInMinutes
			});
		}

		const appointmentEndTimeInMinutes: number = getMinutesSinceMidnight(appointmentEnd, timezone);

		blocksForTheDay.push({
			appointment,
			durationInMinutes: appointmentEndTimeInMinutes - appointmentStartTimeInMinutes,
			startTimeInMinutes: appointmentStartTimeInMinutes,
			endTimeInMinutes: appointmentEndTimeInMinutes
		});
	}

	return result;
}

function getMinutesSinceMidnight(date: Date, timezone: TimeZone): number {
	const startOfTheDay: Date = getStartOfDay(date, timezone);

	return calculateTimeDifference(
		date,
		startOfTheDay,
		"minute"
	);
}
